import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { registerUser } from "../../redux/actions/register";
import { message } from "antd";
import { Button, Checkbox, Col, Form, Input, Row } from "antd";
import classes from "./Register.module.css";
import { inputs } from "./registerForm";
import { HiArrowLeft } from "react-icons/hi2";
import logo from "../../assets/images/LightLogo.webp";
import landing from "../../assets/images/Landing.png";

const Register: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(null);
  const [fullName, setFullName] = useState(null);
  const [agreement, setAgreement] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (location.state?.formData) {
      const { fullName, email, password, agreement } = location.state.formData;
      console.log("Restoring form data:", {
        fullName,
        email,
        password,
        agreement,
      });
      setFullName(fullName || null);
      setEmail(email || "");
      setPassword(password || null);
      setAgreement(agreement || false);
      form.setFieldsValue({
        name: fullName,
        email: email,
        password: password,
        confirm: password,
        agreement: agreement,
      });
    }
  }, [location.state, form]);

  const registerUsers = async (values) => {
    if (isSubmitting) {
      console.log("Submission blocked: already in progress");
      return;
    }

    console.log("Starting registration with values:", values);

    if (!agreement) {
      console.log("Agreement not checked");
      message.error(
        "Ju lutemi pranoni kushtet e përdorimit dhe politikën e privatësisë",
      );
      return;
    }

    setIsSubmitting(true);
    const payload = { fullName, email, password };
    console.log("Dispatching registerUser with payload:", payload);

    try {
      await dispatch(registerUser(payload)); // Handle as a standard Promise
      console.log("Registration successful");
      localStorage.setItem("showWorldHintAfterRegister", "true");

      setAgreement(false);
      setEmail("");
      setFullName(null);
      setPassword(null);
      form.resetFields();
      message.success("Ju lutemi shikoni e-mailin tuaj");
      navigate("/login");
    } catch (error) {
      console.error("Registration failed:", error);
      message.error(
        "Regjistrimi dështoi, Përdoruesi me emailin e dhënë tashmë ekziston.",
      );
    } finally {
      setIsSubmitting(false);
      console.log("Submission complete, isSubmitting reset to false");
    }
  };

  const checkBoxOnChange = (e) => {
    const isChecked = e.target.checked;
    console.log("Agreement checkbox changed to:", isChecked);
    setAgreement(isChecked);
  };

  const navigateToTerms = () => {
    console.log("Navigating to Terms with data:", {
      fullName,
      email,
      password,
      agreement,
    });
    navigate("/terms-of-use", {
      state: {
        formData: { fullName, email, password, agreement },
        from: "/register",
      },
    });
  };

  const navigateToPrivacy = () => {
    console.log("Navigating to Privacy with data:", {
      fullName,
      email,
      password,
      agreement,
    });
    navigate("/privacy-policy", {
      state: {
        formData: { fullName, email, password, agreement },
        from: "/register",
      },
    });
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth > 425 && window.innerWidth <= 768,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 425);
      setIsTablet(window.innerWidth > 425 && window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={classes.mainDiv}>
      <Row>
        <Col span={24} className={classes.backButton}>
          <div
            className={classes.circleButton}
            onClick={() => navigate("/lajmet")}
          >
            <HiArrowLeft fontSize="1.2rem" color="white" />
          </div>
          <div
            className={classes.backTitle}
            onClick={() => navigate("/lajmet")}
          >
            <h2>Rikthehu ne faqe</h2>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={14} className={classes.lefLogoColumn}>
          <div className={classes.logoDiv}>
            <div
              style={{
                width: "84%",
                height: "84%",
                position: "relative",
                backgroundImage: `url(${landing})`,
                backgroundSize: "115% auto",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
              className={classes.logoTextDiv}
            >
              <img
                className={classes.brandLogo}
                src={logo}
                alt="Brieffo logo"
              />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 1,
                }}
              ></div>
              <h3>
                Burim i besueshëm i informacionit për të gjithë shqiptarët
              </h3>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={10}>
          <div className={classes.registerDiv}>
            <div
              style={{ marginTop: isMobile ? "60px" : 0 }}
              className={classes.registerTitle}
            >
              <h1>Krijoni llogarinë tuaj</h1>
              <h4>Vendosni detajet më poshtë</h4>
            </div>
            <Form
              form={form}
              name="register"
              onFinish={registerUsers}
              validateTrigger="onSubmit"
              scrollToFirstError
              className={classes.registerFormShell}
            >
              <Form.Item
                name="name"
                className={classes.styledInput}
                style={{ width: "100%" }}
                {...inputs.name}
              >
                <Input
                  placeholder="Emri juaj i plotë"
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email"
                className={classes.styledInput}
                {...inputs.email}
                style={{ width: "100%" }}
              >
                <Input
                  placeholder="Vendosni email-in tuaj"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Item>
              <Form.Item
                name="password"
                label="Fjalëkalimi"
                className={classes.styledInput}
                {...inputs.password}
                style={{ width: "100%" }}
                hasFeedback
              >
                <Input.Password
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Vendos fjalëkalimin tuaj"
                />
              </Form.Item>
              <Form.Item
                className={classes.styledInput}
                name="confirm"
                label="Konfirmoni Fjalëkalimin"
                {...inputs.confirmPassword}
                dependencies={["password"]}
                style={{ width: "100%", resize: "none" }}
                hasFeedback
              >
                <Input.Password
                  size="large"
                  style={{ resize: "none" }}
                  placeholder="Ju lutemi konfirmojeni fjalëkalimin tuaj"
                />
              </Form.Item>
              <Form.Item
                name="agreement"
                valuePropName="checked"
                className={classes.termsConditions}
                {...inputs.agreement}
              >
                <Checkbox
                  onChange={checkBoxOnChange}
                  style={{ color: "#FFFFFF" }}
                >
                  Kam lexuar dhe pajtohem me{" "}
                  <a style={{ color: "#40A2DE" }} onClick={navigateToTerms}>
                    Kushtet e Përdorimit
                  </a>{" "}
                  &{" "}
                  <a style={{ color: "#40A2DE" }} onClick={navigateToPrivacy}>
                    Politikat e Privatësisë
                  </a>
                </Checkbox>
              </Form.Item>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                style={{ width: "100%" }}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    width: "100%",
                    backgroundColor: "#1677ff", // Set your preferred color
                    borderColor: "#40A2DE", // Match border with background
                    color: "#fff",
                  }}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Regjistrohu
                </Button>
              </Form.Item>
            </Form>
            <div className={classes.footerRegister}>
              <p>
                Keni llogari?{" "}
                <Link to="/login" style={{ color: "#40A2DE" }}>
                  Kyçuni
                </Link>
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Register;
